import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import TissAnalyzer from './components/TissAnalyzer';
import LoadingScreen from './components/LoadingScreen';
import AuthWrapper from './components/AuthWrapper';
import AdminPanel from './components/AdminPanel';

//A configuração do Supabase fica aqui, no topo da árvore de componentes
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

function App() {
  const [session, setSession] = useState(null);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Verificando autenticação...');

useEffect(() => {
  let timeoutId;

  const initializeApp = async () => {
    try {
      //Só roda o loading se for um load real (não apenas voltar pra aba)
      if (performance.navigation.type === performance.navigation.TYPE_RELOAD || 
          performance.navigation.type === performance.navigation.TYPE_NAVIGATE) {
        setLoading(true);
        setLoadingMessage("Verificando autenticação...");
      }

      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);

      //delayzinho pra UX mais suave
      timeoutId = setTimeout(() => setLoading(false), 800);

    } catch (error) {
      console.error("Erro ao inicializar aplicação:", error);
      setAuthError("Erro ao conectar com o servidor. Tente novamente.");
      setLoading(false);
    }
  };

  initializeApp();

  //não mostra loading quando só muda o foco da aba
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      //não faz nada → só mantém estado atual
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    setSession(session);
    setLoading(false);
  });

  return () => {
    subscription.unsubscribe();
    if (timeoutId) clearTimeout(timeoutId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, []);



  const handleLogin = async (email, password) => {
    try {
      setAuthError('');
      setLoadingMessage('Fazendo login...');
      
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      //O listener onAuthStateChange cuidará de atualizar a sessão
    } catch (error) {
      setAuthError(error.message || 'Falha ao fazer login. Verifique suas credenciais.');
    }
  };

  const handleRegister = async ({ userData, companyData }) => {
    try {
      setAuthError('');
      setLoadingMessage('Criando conta...');
      
      //1. Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Erro ao criar usuário');
      }

      setLoadingMessage('Configurando empresa...');

      //2. Verificar se a empresa já existe
      let companyId = null;
      const { data: existingCompany, error: searchError } = await supabase
        .from('companies')
        .select('id')
        .eq('cnpj', companyData.cnpj)
        .single();

      if (searchError && searchError.code !== 'PGRST116') {
        throw searchError;
      }

      if (existingCompany) {
        //Empresa já existe, usar o ID existente
        companyId = existingCompany.id;
      } else {
        //Criar nova empresa - converter campos para snake_case
        const companyDataForDB = {
          cnpj: companyData.cnpj,
          razao_social: companyData.razao_social,
          numero_operadora: companyData.numero_operadora,
          nome_fantasia: companyData.nome_fantasia || null,
          email: companyData.email || null,
          telefone: companyData.telefone || null,
          endereco_logradouro: companyData.endereco_logradouro || null,
          endereco_numero: companyData.endereco_numero || null,
          endereco_complemento: companyData.endereco_complemento || null,
          endereco_bairro: companyData.endereco_bairro || null,
          endereco_cidade: companyData.endereco_cidade || null,
          endereco_uf: companyData.endereco_uf || null,
          endereco_cep: companyData.endereco_cep || null,
          atividade_principal: companyData.atividade_principal || null,
          situacao_cadastral: companyData.situacao_cadastral || null
        };
        
        const { data: newCompany, error: companyError } = await supabase
          .from('companies')
          .insert(companyDataForDB)
          .select('id')
          .single();

        if (companyError) throw companyError;
        companyId = newCompany.id;
      }

      setLoadingMessage('Finalizando cadastro...');

      //3. Atualizar perfil do usuário
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          nome_completo: userData.nomeCompleto,
          telefone: userData.telefone,
          cargo: userData.cargo,
          company_id: companyId
        })
        .eq('id', authData.user.id);

      if (profileError) throw profileError;

      //feedback visual
      await new Promise(resolve => setTimeout(resolve, 1000));

      
    } catch (error) {
      console.error('Erro no cadastro:', error);
      setAuthError(error.message || 'Erro ao criar conta. Tente novamente.');
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {!session ? (
        <AuthWrapper 
          onLogin={handleLogin} 
          onRegister={handleRegister}
          error={authError} 
          supabase={supabase}
        />
      ) : (
        <div className="animate-fade-in">
          <TissAnalyzer key={session.user.id} session={session} supabase={supabase} />
        </div>
      )}
    </div>
  );
}

export default App;